// SearchBox — 검색어 입력 컴포넌트.
// - 입력값은 로컬 state로 즉시 반영 (빠른 UI 응답).
// - 300ms 디바운스 후 URL 쿼리 파라미터 q 갱신.
// - 빈 문자열 입력 시 q 파라미터 제거.
// - lodash 의존 없이 setTimeout 패턴 사용.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface SearchBoxProps {
  /** placeholder 텍스트 */
  placeholder?: string;
  /** 추가 CSS 클래스 */
  className?: string;
}

const DEBOUNCE_MS = 300;

export function SearchBox({ placeholder = '이름·내용으로 검색', className }: SearchBoxProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  // URL에서 초기값 읽기
  const [localValue, setLocalValue] = useState(() => searchParams.get('q') ?? '');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // URL q 파라미터가 외부에서 변경된 경우(뒤로가기 등) 로컬 상태 동기화
  useEffect(() => {
    const urlQ = searchParams.get('q') ?? '';
    setLocalValue(urlQ);
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalValue(val);

    // 기존 디바운스 타이머 취소
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    // 300ms 후 URL 갱신
    timerRef.current = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (val.trim()) {
          next.set('q', val.trim());
        } else {
          next.delete('q');
        }
        // 검색어 변경 시 offset 초기화
        next.delete('offset');
        return next;
      }, { replace: true });
    }, DEBOUNCE_MS);
  };

  const handleClear = () => {
    setLocalValue('');
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('q');
      next.delete('offset');
      return next;
    }, { replace: true });
  };

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <div className={`search-box${className ? ` ${className}` : ''}`}>
      <span className="search-box__icon" aria-hidden="true">🔍</span>
      <input
        type="search"
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        aria-label="검색어 입력"
        className="search-box__input"
      />
      {localValue && (
        <button
          type="button"
          className="search-box__clear"
          onClick={handleClear}
          aria-label="검색어 지우기"
        >
          ✕
        </button>
      )}
    </div>
  );
}
